import type { BookingExecutionMode, BookingSelectedTime, WaitingListSelection } from '@/types/booking-flow';
import { CheckCircle2 } from 'lucide-react';

interface BookingConfirmedStepProps {
  companyAddress: string;
  companyName: string;
  executionMode?: BookingExecutionMode;
  onGoHome: () => void;
  proName: string;
  selectedTime: BookingSelectedTime | null;
  totalPrice: number;
  userName: string;
  waitingListSelection?: WaitingListSelection | null;
}

const BookingConfirmedStep = ({
  companyAddress,
  companyName,
  executionMode = 'booking',
  onGoHome,
  proName,
  selectedTime,
  totalPrice,
  userName,
  waitingListSelection,
}: BookingConfirmedStepProps) => {
  const isWaitingList = executionMode === 'waiting-list';

  return (
    <div className="animate-scale-in py-8 text-center">
      <div className="bg-success/10 mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full">
        <CheckCircle2 className="text-success h-10 w-10" />
      </div>
      <h2 className="text-foreground mb-2 text-2xl font-bold">
        {isWaitingList ? 'Fila de espera confirmada' : 'Agendamento confirmado!'}
      </h2>
      <p className="text-muted-foreground mb-8">
        {isWaitingList
          ? userName
            ? `${userName}, a gente avisa se abrir um horário.`
            : 'A gente avisa se abrir um horário.'
          : userName
            ? `${userName}, seu horário está reservado.`
            : 'Seu horário está reservado.'}
      </p>

      <div className="border-border bg-card mx-auto mb-8 max-w-sm space-y-3 rounded-xl border p-5 text-left">
        <div className="flex items-start justify-between gap-4">
          <span className="text-muted-foreground shrink-0 text-sm">Estabelecimento</span>
          <span className="text-card-foreground min-w-0 text-right text-sm font-medium">{companyName}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground shrink-0 text-sm">Local</span>
          <span className="text-card-foreground text-right text-sm font-medium">
            {companyAddress || 'Endereço não informado'}
          </span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <span className="text-muted-foreground shrink-0 text-sm">{isWaitingList ? 'Dia' : 'Data'}</span>
          <span className="text-card-foreground min-w-0 text-right text-sm font-medium">
            {isWaitingList
              ? waitingListSelection
                ? `${waitingListSelection.dayOfWeek} (${waitingListSelection.date})`
                : '-'
              : `${selectedTime?.label} (${selectedTime?.date})`}
          </span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <span className="text-muted-foreground shrink-0 text-sm">{isWaitingList ? 'Preferência' : 'Horário'}</span>
          <span className="text-card-foreground min-w-0 text-right text-sm font-medium">
            {isWaitingList ? (waitingListSelection?.preferredTime ?? 'Qualquer horário') : selectedTime?.time}
          </span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <span className="text-muted-foreground shrink-0 text-sm">Profissional</span>
          <span className="text-card-foreground min-w-0 text-right text-sm font-medium">{proName}</span>
        </div>
        <div className="border-border flex justify-between border-t pt-3">
          <span className="text-card-foreground text-sm font-semibold">{isWaitingList ? 'Estimativa' : 'Total'}</span>
          <span className="text-primary text-sm font-bold">R$ {totalPrice.toFixed(2).replace('.', ',')}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={onGoHome}
        className="bg-primary text-primary-foreground h-12 rounded-xl px-8 font-semibold transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] hover:opacity-90"
      >
        Voltar ao início
      </button>
    </div>
  );
};

export default BookingConfirmedStep;
